import {
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
    IMAGE_SIZE_ERROR_MESSAGE,
    RichTextEditor,
    hasEditorContent,
    isImageSizeError,
} from "../../../components";
import { useFetch, useSnackbar } from "../../../hooks";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import CheckIcon from "@mui/icons-material/Check";
import CommentIcon from "@mui/icons-material/Comment";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";

export const ProcessingCommentsModal = ({ contract, mutate }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const { openSnackbar } = useSnackbar();

    const { patch } = useFetch();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm({
        defaultValues: {
            processingComments: contract.processingComments,
        },
    });

    const handleOpenDialog = () => {
        // Seeded on every opening, and before the dialog mounts its
        // content: react-hook-form and the Lexical composer only ever
        // read their initial values, so the modal would otherwise keep
        // serving the comments as they were on its first opening.
        reset({ processingComments: contract.processingComments });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const onSubmit = async (data) => {
        const body = {
            processingComments:
                data.processingComments &&
                JSON.stringify(data.processingComments),
        };

        try {
            await mutate(
                patch(
                    `${BACKEND_ROUTES.CONTRACT}/${contract.uuid}?dataAllowances=true&cloverfieldData=true`,
                    {
                        body,
                        forwardError: true,
                    }
                ),
                {
                    populateCache: (updatedContract, currentContract) => ({
                        ...currentContract,
                        ...updatedContract,
                    }),
                    revalidate: false,
                }
            );
            handleCloseDialog();
            openSnackbar("Comments saved successfully", "success");
        } catch (error) {
            openSnackbar(
                isImageSizeError(error)
                    ? IMAGE_SIZE_ERROR_MESSAGE
                    : "An error occurred while saving comments",
                "error"
            );
        }
    };

    return (
        <>
            <Fab
                size="medium"
                onClick={handleOpenDialog}
                color="secondary"
                sx={{
                    position: "fixed",
                    right: 10,
                    bottom: 20,
                }}
            >
                {hasEditorContent(contract.processingComments) ? (
                    <Badge
                        color="primary"
                        badgeContent={<CheckIcon fontSize="small" />}
                        sx={{
                            "& .MuiBadge-badge": {
                                px: 0.5,
                                width: 18,
                                height: 18,
                            },
                        }}
                    >
                        <CommentIcon />
                    </Badge>
                ) : (
                    <CommentIcon />
                )}
            </Fab>
            <Dialog
                fullWidth
                maxWidth="lg"
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle>Processing Comments</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)} id="commentForm">
                        <Controller
                            name="processingComments"
                            control={control}
                            render={({ field }) => (
                                <RichTextEditor
                                    defaultEditorState={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        form="commentForm"
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
